import { GridFilterItem, GridSortModel } from "@platform/style";
import { WORK_ITEMS_BFF_API_BASE } from "../platform-workitems";
import { getLast14DaysDateRange, getOneDayAfter, getOneDayBefore } from "../utils/getDay";
import { WorkItemPageTypes } from "../models/WorkItemPageTypes";

const FilterArray: { [queryColumn: string]: string } = {
  Associations: "associationSearch",
  BusinessFunction: "businessFunctions",
  TitleSearch: "titleSearch",
  TitleEquals: "titleEqualSearch",
  Status: "workItemStatuses",
  BusinessProcess: "businessProcesses",
  CreatedDatetimeStart: "createStartDate",
  CreatedDatetimeEnd: "createEndDate",
  DueDateStart: "dueStartDate",
  DueDateEnd: "dueEndDate",
  FollowUpDate: "followupDate",
  AssignedTo: "assignees",
  ClosedBy: "closedByUsers",
  ResolutionReasonCode: "closedResolutionCodes",
  ClosedDatetimeStart: "closedStartDate",
  ClosedDatetimeEnd: "closedEndDate",
};

const SortArray: { [queryColumn: string]: string } = {
  BusinessFunction: "businessFunctions",
  Title: "title",
  Status: "workItemStatuses",
  BusinessProcess: "businessProcesses",
  CreatedDatetime: "createdDate",
  DueDate: "dueDate",
  FollowUpDate: "followupDate",
  ResolutionReasonCode: "closedResolutionCodes",
  ClosedDatetime: "closedDate",
  Score: "score",
};

const inactiveWorkItemStatuses = "COMPLETED,CANCELLED";
const activeWorkItemStatuses = "IN_PROGRESS,ON_HOLD,NEW";

const inactiveWorkItemDefaultSort = "closedDate:desc";
const activeWorkItemDefaultSort = "score:desc,createdDate:asc";

function getBaseUri(pageSize: number, pageNumber: number): string {
  return `${WORK_ITEMS_BFF_API_BASE}?pageSize=${pageSize}&page=${pageNumber}`;
}

function getSortByParam(sortParams: string, defaultSort: string): string {
  return `&sortBy=${sortParams ? sortParams + "," : ""}${defaultSort}`;
}

function addStatusesAndSortParams(
  computedURI: string,
  statuses: string,
  sortParams: string,
  defaultSort: string
): string {
  if (!computedURI.includes("workItemStatuses")) {
    computedURI += `&workItemStatuses=${statuses}${getSortByParam(sortParams, defaultSort)}`;
  } else {
    computedURI += getSortByParam(sortParams, defaultSort);
  }
  return computedURI;
}

function addParamsByPageType(
  computedURI: string,
  typeOfWorkItemPage: string,
  sortParams: string
): string {
  switch (typeOfWorkItemPage) {
    case WorkItemPageTypes.InactivePage:
      return addStatusesAndSortParams(
        computedURI,
        inactiveWorkItemStatuses,
        sortParams,
        inactiveWorkItemDefaultSort
      );
    case WorkItemPageTypes.ActivePage:
      return addStatusesAndSortParams(
        computedURI,
        activeWorkItemStatuses,
        sortParams,
        activeWorkItemDefaultSort
      );
    case WorkItemPageTypes.MyWorkPage:
      computedURI += `&assignees=self`;
      return addStatusesAndSortParams(
        computedURI,
        activeWorkItemStatuses,
        sortParams,
        activeWorkItemDefaultSort
      );
    default:
      return computedURI;
  }
}

function generateSortParams(sortModel?: GridSortModel): string {
  if (sortModel && sortModel.length > 0) {
    return sortModel
      .map((sortByParam) => `${SortArray[sortByParam.field]}:${sortByParam.sort}`)
      .join(",");
  }
  return "";
}

export function FilterAndSortGridRecords(
  typeOfWorkItemPage: string,
  pageSize: number,
  pageNumber: number,
  filterItems?: GridFilterItem[] | undefined,
  sortModel?: GridSortModel | undefined
) {
  const sortParams = generateSortParams(sortModel);
  let computedUri = getBaseUri(pageSize, pageNumber);

  if (
    typeOfWorkItemPage === WorkItemPageTypes.InactivePage &&
    (!filterItems || filterItems.length === 0)
  ) {
    const { startDate, endDate } = getLast14DaysDateRange();
    computedUri += `&${FilterArray["ClosedDatetimeStart"]}=${startDate}&${FilterArray["ClosedDatetimeEnd"]}=${endDate}`;
  }

  filterItems?.forEach((item) => {
    if (item.value?.trim() !== "" && item.value !== undefined) {
      switch (item.field) {
        case "CreatedDatetime":
        case "ClosedDatetime":
        case "DueDate":
          if (item.operator == "is") {
            computedUri += `&${FilterArray[item.field + "Start"]}=${item.value}&${
              FilterArray[item.field + "End"]
            }=${item.value}`;
          }
          if (item.operator == "after") {
            computedUri += `&${FilterArray[item.field + "Start"]}=${getOneDayAfter(item.value)}`;
          }
          if (item.operator == "onOrAfter") {
            computedUri += `&${FilterArray[item.field + "Start"]}=${item.value}`;
          }
          if (item.operator == "onOrBefore") {
            computedUri += `&${FilterArray[item.field + "End"]}=${item.value}`;
          }
          if (item.operator == "before") {
            computedUri += `&${FilterArray[item.field + "End"]}=${getOneDayBefore(item.value)}`;
          }
          break;

        case "Associations":
          computedUri += `&${FilterArray[item.field]}=${
            item.value.includes("&") ? item.value.replace(/&/g, "%26") : item.value
          }`;
          break;

        case "FollowUpDate":
        case "BusinessFunction":
        case "Status":
        case "BusinessProcess":
        case "AssignedTo":
        case "ClosedBy":
        case "ResolutionReasonCode":
          computedUri += `&${FilterArray[item.field]}=${item.value}`;
          break;

        case "Title":
          if (item.operator == "contains") {
            computedUri += `&${FilterArray[item.field + "Search"]}=${item.value}`;
          } else {
            computedUri += `&${FilterArray[item.field + "Equals"]}=${item.value}`;
          }
          break;
      }
    }
  });

  computedUri = addParamsByPageType(computedUri, typeOfWorkItemPage, sortParams);

  return computedUri;
}
